import { useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { api, ApiError, newIdempotencyKey, type ScanResult } from '../lib/api'
import { Screen, Button, Notice, Field, Staleness } from '../components/Layout'

/**
 * Charger confirmation — PRD v1.1 §15.2.
 *
 * The customer must explicitly confirm before anything is charged, and must be able to
 * answer "where am I charging, which connector, is it free, what does it cost" from
 * this one screen (§99).
 */
export default function Confirm() {
  const navigate = useNavigate()
  const scan = (useLocation().state ?? null) as ScanResult | null

  const [connectorId, setConnectorId] = useState<number | null>(
    scan?.connector_id ?? null,
  )
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Minted once per confirmation screen, not per tap: a retry must be the *same*
  // request, or one tap becomes two sessions (§34.2).
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [])

  if (!scan) return <Navigate to="/charging/start" replace />

  const chosen = scan.connectors.find((c) => c.connector_id === connectorId)
  const staleSeconds = chosen?.observed_at
    ? (Date.now() - new Date(chosen.observed_at).getTime()) / 1000
    : null

  const blocked =
    !scan.online || connectorId === null || (chosen ? !chosen.available : false)

  async function start() {
    if (connectorId === null) return
    setBusy(true)
    setError(null)
    try {
      const session = await api.startCharging(scan!.evse_id, connectorId, idempotencyKey)
      navigate(`/charging/${session.session_id}`, { replace: true })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '無法開始充電，請再試一次。')
      setBusy(false)
    }
  }

  return (
    <Screen title="確認充電樁" back="/charging/start">
      <section className="card">
        <Field label="充電站" value={scan.station_name ?? '—'} />
        <Field label="充電樁" value={scan.evse_id} />
        <Field
          label="狀態"
          value={scan.online ? (chosen?.status ?? '可使用') : '離線'}
        />
      </section>

      {scan.needs_connector_choice && (
        <section className="card">
          <h2>請選擇接頭</h2>
          {/* Guessing the connector starts the wrong cable, so we always ask. */}
          <div className="grid">
            {scan.connectors.map((c) => (
              <button
                key={c.connector_id}
                className={`chip ${connectorId === c.connector_id ? 'chip-on' : ''}`}
                disabled={!c.available}
                onClick={() => setConnectorId(c.connector_id)}
              >
                接頭 {c.connector_id}
                <small>{c.available ? '可使用' : c.status}</small>
              </button>
            ))}
          </div>
        </section>
      )}

      <Staleness seconds={staleSeconds} />

      {!scan.online && (
        <Notice tone="error">此充電樁目前離線，請稍後再試或使用其他充電樁。</Notice>
      )}
      {scan.online && chosen && !chosen.available && (
        <Notice tone="error">此接頭目前無法使用，請選擇其他接頭。</Notice>
      )}
      {error && <Notice tone="error">{error}</Notice>}

      <Button onClick={start} disabled={blocked} busy={busy}>
        確認並開始充電
      </Button>
    </Screen>
  )
}
